import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";
import Header from "../components/Header";
import "../styles/global.css";
import ReactGA from 'react-ga4';
import "../styles/home.css";
import { Helmet } from "react-helmet";
const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Relationship Wellness | Relationship Quiz | Should I Breakup Test";
    ReactGA.send({ hitType: 'pageview', page: window.location.pathname });
  }, []);
  const navigate = useNavigate();
  const controls = useAnimation();
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);

  const cards = [
    {
      icon: "fa-wind",
      title: "Loss of Inner Peace",
      description:
        "Constant conflict and negativity disrupts your mental balance and emotional wellbeing",
      color: "#8a2be2",
    },
    {
      icon: "fa-clock",
      title: "Wasted Potential",
      description:
        "Every moment spent in an unfulfilling relationship is time you'll never get back",
      color: "#ff3e78",
    },
    {
      icon: "fa-heartbeat",
      title: "Emotional Damage",
      description:
        "Chronic dissatisfaction can lead to depression, anxiety, and diminished self-worth",
      color: "#00d4a1",
    },
    {
      icon: "fa-heart-broken",
      title: "Missed Connections",
      description:
        "Staying trapped prevents you from finding a relationship that truly nurtures your soul",
      color: "#ff7c50",
    },
  ];

  const articles = [
    {
      id: "who-should-take",
      title: "Who Should Take This Assessment?",
      content: [
        "This relationship assessment is designed for anyone experiencing uncertainty, dissatisfaction, or conflict in their romantic relationship. It's particularly valuable for:",
        "• Individuals questioning if their relationship has a healthy future",
        "• Those experiencing persistent feelings of unhappiness despite trying to make things work",
        "• People who feel consistently unvalued, unheard, or misunderstood by their partner",
        "• Anyone who suspects they might be in a toxic or emotionally harmful relationship",
        "• Couples who argue frequently without resolution or growth",
        "The assessment provides objective insights based on psychological research, helping you gain clarity about your relationship dynamics and potential paths forward.",
      ],
      // image: "assets/images/couple-crossroads.jpeg",
      icon: "fa-question-circle",
    },
    {
      id: "signs-to-leave",
      title: "Signs It May Be Time to Leave Your Relationship",
      content: [
        "While relationships require work, certain patterns indicate fundamental incompatibility or unhealthy dynamics:",
        "• <strong>Persistent Disrespect:</strong> Your boundaries, opinions, or needs are regularly dismissed or belittled",
        "• <strong>Emotional or Physical Abuse:</strong> Any form of violence, manipulation, or controlling behavior is never acceptable",
        "• <strong>Chronic Dishonesty:</strong> Repeated lying damages trust beyond repair",
        "• <strong>Fundamentally Different Values:</strong> Incompatible core beliefs about life's most important aspects",
        "• <strong>Constant Exhaustion:</strong> The relationship consistently drains rather than energizes you",
        "• <strong>Lost Sense of Self:</strong> You've abandoned your identity, interests, or growth to preserve the relationship",
        "Remember that ending a relationship doesn't represent failure, but rather an act of self-respect and courage.",
      ],
      image: "assets/images/separate-paths.jpeg",
      icon: "fa-door-open",
    },
    {
      id: "self-worth",
      title: "Reclaiming Your Self-Worth",
      content: [
        "Many people remain in unfulfilling relationships because they've internalized false beliefs about their value and what they deserve.",
        "The truth is that everyone deserves to be with someone who:",
        "• Consistently treats them with respect and kindness",
        "• Values their thoughts, feelings, and contributions",
        "• Supports their personal growth and happiness",
        "• Demonstrates love through both words and actions",
        "• Is willing to work through challenges with honesty and openness",
        "Our assessment helps you recognize patterns that may be diminishing your sense of worth and provides guidance for rebuilding a healthy relationship with yourself—which is the foundation for any healthy partnership with others.",
      ],
      image: "assets/images/self-worth.jpeg",
      icon: "fa-crown",
    },
  ];

  useEffect(() => {
    const sequence = async () => {
      await controls.start({
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.8 },
      });
    };
    sequence();

    // Initialize AdSense
  // Initialize AdSense
  setTimeout(() => {
    try {
      if (window.adsbygoogle && process.env.NODE_ENV !== 'development') {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      console.error('AdSense error:', e);
    }
  }, 2000);
    // Handle scroll indicator
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowScrollIndicator(false);
      } else {
        setShowScrollIndicator(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [controls]);
  const handleAssessmentClick = () => {
    ReactGA.event({
      category: 'User Interaction',
      action: 'Click',
      label: 'Start Free Assessment Button',
    });
    navigate('/quiz');
  };
  return (
    <div className="home-container">
      <div className="gradient-background"></div>
      <Header />
      <Helmet>
  <title>Relationship Wellness |Relationship Wellness Quiz |Relationship Wellness Test</title>
  <meta name="description" content="Explore signs of a healthy relationship, red flags, and take our relationship quiz to decide if you should break up or stay." />
  <meta property="og:title" content="Relationship Wellness Quiz by Nivexa" />
  <meta property="og:description" content="Explore signs of a healthy relationship, red flags, and take our relationship quiz to decide if you should break up." />
  <meta property="og:url" content="https://shouldibreakup.nivexa.net/" />
  <meta property="og:type" content="website" />
  <link rel="canonical" href="https://shouldibreakup.nivexa.net/" />
  <meta name="keywords" content="should I break up, love test, relationship quiz, healthy relationships, signs of a healthy relationship, when to break up, relationship red flags, toxic relationship signs, relationship compatibility, relationship counseling, how to fix a relationship, relationship advice, relationship boundaries, communication in relationships, relationship problems, signs of a failing relationship, relationship therapy, dating advice, emotional intimacy, trust in relationships, relationship conflict resolution, couples therapy, how to improve relationship, relationship assessment, love languages, am I in love quiz, relationship check-up, healthy boundaries in relationships, relationship experts, should I stay or leave quiz, signs they love you, how to know if relationship is over, relationship test, am I in a toxic relationship, how to know if you should break up, compatibility quiz, signs your relationship is over, relationship advice for women, relationship advice for men, how to fix a broken relationship, love calculator, relationship goals, online relationship counseling, free relationship quiz online, should I stay or go relationship quiz, signs of an unhealthy relationship checklist, how to improve communication in a relationship, relationship red flags checklist" />
  <script type="application/ld+json">
    {`
      {
        "@context": "https://schema.org",
        "@type": "Website",
        "name": "Relationship Wellness",
        "url": "https://shouldibreakup.nivexa.net/"
      }
    `}
  </script>
</Helmet>
      <section className="hero-section">
        <div className="content-container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={controls}>
            <h1 className="title">
              Is Your Relationship{" "}
              <span className="accent-text">Worth Saving?</span>
            </h1>
            <p className="subtitle">
              Gain evidence-based clarity about your relationship and discover
              whether to work through challenges or move forward alone
            </p>
            <motion.button
              className="primary-button"
              onClick={handleAssessmentClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}>
              <span>Start Free Assessment</span>
              <i className="fas fa-arrow-right"></i>
            </motion.button>
          </motion.div>

          <div className="hero-image fade-in-scale">
  <picture>
    <source srcSet="/assets/images/couple-crossroads.jpeg 480w, /assets/images/couple-crossroads.jpeg 800w" type="image/webp" />
    <img
      src="/assets/images/couple-crossroads.jpeg"
      alt="Relationship Crossroads"
      sizes="(max-width: 600px) 480px, 800px"
      loading="lazy"
      width="800"
      height="400"
    />
  </picture>
</div>
        </div>

        <motion.div
          className="scroll-indicator"
          animate={{
            y: [0, 10, 0],
            opacity: showScrollIndicator ? 1 : 0,
          }}
          transition={{
            y: { repeat: Infinity, duration: 1.5 },
            opacity: { duration: 0.3 },
          }}>
          <span style={{ color: "#000" }}>Scroll to learn more</span>
          <i className="fas fa-chevron-down"></i>
        </motion.div>
      </section>

      {/* Top AdSense Banner */}
      <div
        className="ad-container top-ad"
        style={{ width: "100%", minWidth: "300px" }}>
        {/* <div className="ad-label">Advertisement</div> */}
        <ins
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client="ca-pub-2067522743161930"
          data-ad-slot="1234567890"
          data-ad-format="auto"
          data-full-width-responsive="true"></ins>
      </div>

      <section className="costs-section">
        <div className="content-container">
          <div className="section-header">
            <h2>The Hidden Costs of Staying in the Wrong Relationship</h2>
            <p style={{ color: "#000" }}>
              Recognizing what you sacrifice by remaining in an unfulfilling
              partnership
            </p>
          </div>

          <div className="cards-grid">
            {cards.map((card, index) => (
              <motion.div
                key={index}
                className="card-modern"
                style={{ "--card-accent": card.color }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ delay: index * 0.1, duration: 0.5 }}>
                <div className="card-icon-wrapper">
                  <i className={`fas ${card.icon}`}></i>
                </div>
                <div className="card-content">
                  <h3 className="card-title">{card.title}</h3>
                  <p className="card-description">{card.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid-page AdSense - In-article ad */}
      <div
        className="ad-container in-article-ad"
        style={{ width: "100%", minWidth: "300px" }}>
        {/* <div className="ad-label">Advertisement</div> */}
        <ins
          className="adsbygoogle"
          style={{ display: "block", textAlign: "center" }}
          data-ad-layout="in-article"
          data-ad-format="fluid"
          data-ad-client="ca-pub-2067522743161930"
          data-ad-slot="1234567891"></ins>
      </div>

      <section className="assessment-cta">
        <div className="content-container">
          <motion.div
            className="cta-box"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}>
            <div className="cta-content">
              <div className="cta-badge">
                <span>Evidence-Based</span>
              </div>
              <h2>Our assessment is built on psychological research</h2>
              <p style={{ color: "#FFF" }}>
                Developed by relationship experts, this assessment evaluates key
                dimensions of relationship health and compatibility to provide
                meaningful insights about your situation.
              </p>
              <ul className="feature-list">
                <li>
                  <i className="fas fa-check-circle"></i>{" "}
                  <span>Confidential and judgment-free</span>
                </li>
                <li>
                  <i className="fas fa-check-circle"></i>{" "}
                  <span>Takes only 5 minutes to complete</span>
                </li>
                <li>
                  <i className="fas fa-check-circle"></i>{" "}
                  <span>Provides actionable next steps</span>
                </li>
                <li>
                  <i className="fas fa-check-circle"></i>{" "}
                  <span>Used by over 10,000 people</span>
                </li>
              </ul>
              <motion.button
                className="primary-button"
                onClick={() => navigate("/quiz")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}>
                <span>Begin Assessment</span>
                <i className="fas fa-arrow-right"></i>
              </motion.button>
            </div>
            <div className="cta-decoration">
              <div className="floating-shape shape1"></div>
              <div className="floating-shape shape2"></div>
              <div className="floating-shape shape3"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sidebar AdSense */}
      <div className="content-container with-sidebar">
        <div className="main-content">
          <section className="articles-section">
            <div className="section-header">
              <h2>Understanding Relationship Dynamics</h2>
              <p style={{ color: "#000" }}>
                Knowledge is the first step toward making confident decisions
                about your future
              </p>
            </div>

            {articles.map((article, index) => (
              <motion.article
                key={article.id}
                className="info-article"
                id={article.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7 }}>
                <div className="article-icon">
                  <i className={`fas ${article.icon}`}></i>
                </div>
                <div className="article-content">
                  <h3>{article.title}</h3>
                  {article.content.map((paragraph, pIndex) => (
                    <p
                      key={pIndex}
                      dangerouslySetInnerHTML={{ __html: paragraph }}></p>
                  ))}
                </div>

                {/* In-article ad after first and second articles */}
                {(index === 0 || index === 1) && (
                  <div
                    className="ad-container in-content-ad"
                    style={{ width: "100%", minWidth: "300px" }}>
                    {/* <div className="ad-label">Advertisement</div> */}
                    <ins
                      className="adsbygoogle"
                      style={{ display: "block" }}
                      data-ad-client="ca-pub-2067522743161930"
                      data-ad-slot={`123456789${index + 2}`}
                      data-ad-format="auto"
                      data-full-width-responsive="true"></ins>
                  </div>
                )}
              </motion.article>
            ))}
          </section>

          <section className="testimonials-section">
            <div className="section-header">
              <h2>Stories of Clarity</h2>
              <p style={{ color: "#000" }}>
                Read how others found direction through our relationship
                assessment
              </p>
            </div>

            <div className="testimonials-container">
              <motion.div
                className="testimonial"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}>
                <div className="testimonial-content">
                  <div className="quote-mark">"</div>
                  <p style={{ color: "#000" }}>
                    After months of confusion and sleepless nights, this
                    assessment helped me see the patterns in my relationship
                    clearly. I realized we weren't growing together and made the
                    difficult but necessary decision to move on. Six months
                    later, I'm happier than I've been in years.
                  </p>
                  <div className="testimonial-author">
                    <div className="author-image">
                      <img
                        src="assets/images/testimonial-1.jpeg"
                        alt="Sarah K."
                      />
                    </div>
                    <div className="author-info">
                      <p style={{ color: "#000" }}>Sarah K.</p>
                      <p style={{ color: "#000" }}>32, Marketing Executive</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="testimonial"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.5 }}>
                <div className="testimonial-content">
                  <div className="quote-mark">"</div>
                  <p style={{ color: "#000" }}>
                    The assessment highlighted communication issues I hadn't
                    fully acknowledged. Instead of leaving, I showed the results
                    to my partner, and it became the starting point for the most
                    honest conversations we've ever had. We're now in couples
                    therapy and making real progress.
                  </p>
                  <div className="testimonial-author">
                    <div className="author-image">
                      <img
                        src="assets/images/testimonial-2.jpeg"
                        alt="Michael T."
                      />
                    </div>
                    <div className="author-info">
                      <p style={{ color: "#000" }}>Michael T.</p>
                      <p style={{ color: "#000" }}>28, Software Engineer</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        </div>

        <aside className="sidebar">
          <div className="sticky-sidebar">
            <div className="sidebar-widget">
              <h3>Take The First Step</h3>
              <p>
                Our research-based assessment will help you gain clarity about
                your relationship in just 5 minutes.
              </p>
              <button
                className="secondary-button"
                onClick={() => navigate("/quiz")}>
                Start Assessment
              </button>
            </div>

            <div
              className="ad-container sidebar-ad"
              style={{ width: "100%", minWidth: "300px" }}>
              {/* <div className="ad-label">Advertisement</div> */}
              <ins
                className="adsbygoogle"
                style={{ display: "block" }}
                data-ad-client="ca-pub-2067522743161930"
                data-ad-slot="1234567895"
                data-ad-format="auto"
                data-full-width-responsive="true"></ins>
            </div>

            <div className="sidebar-widget resources">
              <h3>Additional Resources</h3>
              <ul>
                <li>
                  <i className="fas fa-book"></i>
                  <a href="/blog/healthy-relationship-signs">
                    10 Signs of a Healthy Relationship
                  </a>
                </li>
                <li>
                  <i className="fas fa-heart"></i>
                  <a href="/blog/self-care-after-breakup">
                    Self-Care After a Breakup
                  </a>
                </li>
                <li>
                  <i className="fas fa-comments"></i>
                  <a href="/blog/communication-techniques">
                    Effective Communication Techniques
                  </a>
                </li>
                <li>
                  <i className="fas fa-balance-scale"></i>
                  <a href="/blog/boundaries-in-relationships">
                    Setting Healthy Boundaries
                  </a>
                </li>
              </ul>
            </div>
            {/*             
            <div className="sidebar-widget newsletter">
              <h3>Get Relationship Insights</h3>
              <p>Join our newsletter for expert advice and support</p>
              <form className="newsletter-form">
                <input type="email" placeholder="Your email address" />
                <button type="submit">Subscribe</button>
              </form>
            </div> */}
          </div>
        </aside>
      </div>

      {/* Bottom AdSense */}
      <div
        className="ad-container bottom-ad"
        style={{ width: "100%", minWidth: "300px" }}>
        {/* <div className="ad-label">Advertisement</div> */}
        <ins
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client="ca-pub-2067522743161930"
          data-ad-slot="1234567896"
          data-ad-format="auto"
          data-full-width-responsive="true"></ins>
      </div>

      <section className="final-cta">
        <div className="content-container">
          <motion.div
            className="final-cta-content"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}>
            <h2>Ready to gain clarity about your relationship?</h2>
            <p>
              Whether you're seeking confirmation about a difficult decision or
              hoping to identify areas for improvement, our assessment gives you
              the insights you need to move forward with confidence.
            </p>
            <motion.button
              className="primary-button large"
              onClick={() => navigate("/quiz")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}>
              <span>Begin Free Assessment Now</span>
              <i className="fas fa-arrow-right"></i>
            </motion.button>
          </motion.div>
        </div>
      </section>

      <footer className="site-footer">
        <div className="content-container">
          <div className="footer-content">
            <div className="footer-column brand">
              <h3> Relationship Wellness</h3>
              <p>
                Evidence-based insights for healthier relationships and personal
                growth.
              </p>
              <div className="social-icons">
                <a href="#" aria-label="Facebook">
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a href="#" aria-label="Instagram">
                  <i className="fab fa-instagram"></i>
                </a>
                <a href="#" aria-label="Twitter">
                  <i className="fab fa-twitter"></i>
                </a>
                <a href="#" aria-label="Pinterest">
                  <i className="fab fa-pinterest-p"></i>
                </a>
              </div>
            </div>

            <div className="footer-column links">
              <h4>Quick Links</h4>
              <ul>
                <li>
                  <a href="/about">About Us</a>
                </li>
                <li>
                  <a href="/blog">Relationship Blog</a>
                </li>
                <li>
                  <a href="/contact">Contact Us</a>
                </li>
                <li>
                  <a href="/faq">FAQ</a>
                </li>
              </ul>
            </div>

            <div className="footer-column links">
              <h4>Resources</h4>
              <ul>
                <li>
                  <a href="/resources/therapy">Find a Therapist</a>
                </li>
                <li>
                  <a href="/resources/books">Recommended Books</a>
                </li>
                <li>
                  <a href="/resources/support-groups">Support Groups</a>
                </li>
                <li>
                  <a href="/resources/crisis-help">Crisis Help</a>
                </li>
              </ul>
            </div>
            {/*             
            <div className="footer-column newsletter">
              <h4>Stay Updated</h4>
              <p>Get our latest relationship advice and resources</p>
              <form className="footer-form">
                <input type="email" placeholder="Your email address" />
                <button type="submit"><i className="fas fa-paper-plane"></i></button>
              </form>
            </div> */}
          </div>

          <div className="footer-bottom">
            <p className="disclaimer">
              This tool provides insights based on relationship psychology but
              doesn't replace professional advice. If you're experiencing abuse
              or feel unsafe, please contact the National Domestic Violence
              Hotline at 1-800-799-7233.
            </p>
            <div className="legal-links">
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms of Use</a>
              <a href="/cookies">Cookie Policy</a>
            </div>
            <p className="copyright">
              © {new Date().getFullYear()} Relationship Wellness. All rights
              reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
